import moment from 'moment';

/**
 * Definition einer Statistik-Auswertung (CARE-814): nur Daten, kein Code.
 * Die Grenzen entsprechen der Pruefung in der Middleware, damit das Formular
 * nichts abschickt, was der Server ablehnt.
 */

export const MAX_MEASURES = 4;
export const MAX_DIMENSIONS = 3;

export const EMPTY_DEFINITION = {
  MeasureKeys: [],
  DimensionKeys: [],
  ColumnDimensionKeys: [],
  From: null,
  To: null,
};

function keyList(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

function dateOrNull(value) {
  return typeof value === 'string' && value !== '' ? value : null;
}

/** Spalten sind eine Teilmenge der Dimensionen; alles andere faellt weg. */
export function normalizeDefinition(definition) {
  const source = definition || {};
  const dimensionKeys = keyList(source.DimensionKeys);
  return {
    MeasureKeys: keyList(source.MeasureKeys),
    DimensionKeys: dimensionKeys,
    ColumnDimensionKeys: keyList(source.ColumnDimensionKeys)
      .filter((key) => dimensionKeys.includes(key))
      .slice(0, 1),
    From: dateOrNull(source.From),
    To: dateOrNull(source.To),
  };
}

/**
 * Entfernt Schluessel, die der Katalog nicht (mehr) kennt, und nennt sie.
 * Eine gespeicherte Auswertung kann aelter sein als der aktuelle Katalog.
 */
export function restrictToCatalog(definition, catalog) {
  const measureKeys = ((catalog && catalog.Measures) || []).map(
    (item) => item.Key,
  );
  const dimensionKeys = ((catalog && catalog.Dimensions) || []).map(
    (item) => item.Key,
  );
  const source = normalizeDefinition(definition);
  const unknownKeys = [
    ...source.MeasureKeys.filter((key) => !measureKeys.includes(key)),
    ...source.DimensionKeys.filter((key) => !dimensionKeys.includes(key)),
  ];

  return {
    definition: normalizeDefinition({
      ...source,
      MeasureKeys: source.MeasureKeys.filter((key) =>
        measureKeys.includes(key),
      ),
      DimensionKeys: source.DimensionKeys.filter((key) =>
        dimensionKeys.includes(key),
      ),
    }),
    unknownKeys,
  };
}

export function definitionProblems(definition) {
  const source = normalizeDefinition(definition);
  const problems = [];

  if (source.MeasureKeys.length === 0) {
    problems.push('Bitte mindestens eine Kennzahl wählen.');
  }
  if (source.MeasureKeys.length > MAX_MEASURES) {
    problems.push(`Bitte höchstens ${MAX_MEASURES} Kennzahlen wählen.`);
  }
  if (source.DimensionKeys.length > MAX_DIMENSIONS) {
    problems.push(`Bitte höchstens ${MAX_DIMENSIONS} Dimensionen wählen.`);
  }
  // ISO-Datumsangaben lassen sich als Text vergleichen.
  if (source.From && source.To && source.From > source.To) {
    problems.push('Das Von-Datum liegt nach dem Bis-Datum.');
  }

  return problems;
}

/**
 * Gleiche Auswahl fuer den Server? Die Spaltendimension ist reine
 * Darstellung und macht ein Ergebnis deshalb nicht veraltet.
 */
export function sameSelection(left, right) {
  const a = normalizeDefinition(left);
  const b = normalizeDefinition(right);
  return (
    a.MeasureKeys.join('|') === b.MeasureKeys.join('|') &&
    a.DimensionKeys.join('|') === b.DimensionKeys.join('|') &&
    a.From === b.From &&
    a.To === b.To
  );
}

function germanDate(value) {
  return moment(value, 'YYYY-MM-DD', true).format('DD.MM.YYYY');
}

export function describePeriod(definition) {
  const { From, To } = normalizeDefinition(definition);
  if (From && To) {
    return `${germanDate(From)} – ${germanDate(To)}`;
  }
  if (From) {
    return `ab ${germanDate(From)}`;
  }
  if (To) {
    return `bis ${germanDate(To)}`;
  }
  return 'unbegrenzt';
}
